import { ReactNode, Fragment } from 'react';
import { Card, Stack, Button, ButtonGroup, Divider } from '@servicetitan/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import AnimateHeight from 'react-animate-height';
import { Confirm } from '@servicetitan/confirm';
import { CustomConfirmCancel } from '../helper-components/custom-cancel-confirm';
import * as Styles from './flow-card-styles.module.less';

export interface FlowCardCommonProps {
    isOpen: boolean;
    index: string;
    header: ReactNode;
    isComplete: boolean;
    isSkipped?: boolean;
    isReadonly?: boolean;
    active: boolean;
    description?: string;
    activeDescription?: ReactNode;
    completedInfo?: ReactNode;
    error?: ReactNode;
    hasError?: boolean;
    children?: ReactNode;
    className?: string;
    needSecondaryButtonConfirmation?: boolean;
    openHandler?(): void;
}

interface FlowCardWrapperProps extends FlowCardCommonProps {
    disablePrimaryButton?: boolean;
    hidePrimaryButton?: boolean;
    primaryButtonName?: string;
    secondButtonName?: string;
    headerElement: ReactNode;
    animateDuration?: number;
    onPrimaryButtonClick?(): void;
    onSecondaryButtonClick?(): void;
}

export const FlowCardWrapper = observer((props: FlowCardWrapperProps) => {
    const {
        isOpen,
        isComplete,
        isReadonly,
        children,
        active,
        hasError,
        error,
        onSecondaryButtonClick,
        onPrimaryButtonClick,
        needSecondaryButtonConfirmation,
        disablePrimaryButton,
        hidePrimaryButton,
        completedInfo,
        primaryButtonName,
        secondButtonName,
        animateDuration,
        className,
    } = props;

    const forceOpenCurrentStep = () => {
        if (!isComplete && active && !isOpen && !isReadonly && props.openHandler) {
            props.openHandler();
        }
    };

    const duration = animateDuration !== undefined ? animateDuration : 500;
    return (
        <Card
            data-cy="flow-card-wrapper"
            className={classNames(className, Styles.dashboardCard, 'm-t-1')}
        >
            <Card.Section
                className={classNames('p-4', {
                    [Styles.active]: isOpen,
                    'cursor-pointer': !isComplete && !isOpen,
                })}
                onClick={forceOpenCurrentStep}
            >
                <Stack direction="column">
                    {!active && !isComplete && <div className={Styles.disabled} />}
                    {props.headerElement}
                    {isComplete && !isOpen && !isReadonly && completedInfo && (
                        <AnimateHeight duration={500} height={isComplete && !isOpen ? 'auto' : 0}>
                            <div className="m-l-2 m-t-2 p-l-3">{completedInfo}</div>
                        </AnimateHeight>
                    )}
                    <AnimateHeight
                        className={isOpen ? 'm-l-2 m-t-2 p-l-3' : ''}
                        duration={duration}
                        height={isOpen ? 'auto' : 0}
                    >
                        {hasError && error}
                        {children}
                        {onPrimaryButtonClick && (
                            <Fragment>
                                {!hidePrimaryButton && <Divider spacing={3} />}
                                <ButtonGroup>
                                    {!hidePrimaryButton && (
                                        <Button
                                            primary
                                            disabled={disablePrimaryButton}
                                            onClick={onPrimaryButtonClick}
                                        >
                                            {primaryButtonName || 'Next'}
                                        </Button>
                                    )}
                                    {onSecondaryButtonClick && (
                                        <Confirm
                                            title="Are you sure you want to cancel?"
                                            message="Your changes in the current section won’t be saved."
                                            when={needSecondaryButtonConfirmation}
                                            onConfirm={onSecondaryButtonClick}
                                            confirmation={CustomConfirmCancel}
                                        >
                                            {onCancelHandler => (
                                                <Button fill="subtle" onClick={onCancelHandler}>
                                                    {secondButtonName || 'Cancel'}
                                                </Button>
                                            )}
                                        </Confirm>
                                    )}
                                </ButtonGroup>
                            </Fragment>
                        )}
                    </AnimateHeight>
                </Stack>
            </Card.Section>
        </Card>
    );
});
